import { Injectable } from '@angular/core';
import { Params } from '@angular/router';

export type ReadTrackingSource =
  | 'notification'
  | 'homepage'
  | 'homepage_notification_rebound'
  | 'homepage_app_open'
  | 'search'
  | 'suggestion'
  | 'internal_navigation'
  | 'banner_elected_selection'
  | 'banner_top_articles'
  | 'banner_top_faq';

export type ReadTrackingBannerSource =
  | 'banner_elected_selection'
  | 'banner_top_articles'
  | 'banner_top_faq';

export type ReadTrackingSessionEntry = 'notification' | 'app_open';

export type ReadTrackingOriginType = 'article' | 'faq';

export interface ReadTrackingPayload {
  source?: ReadTrackingSource;
  session_entry?: ReadTrackingSessionEntry;
  origin_type?: ReadTrackingOriginType;
  origin_uuid?: string;
  search_id?: number;
}

@Injectable({
  providedIn: 'root',
})
export class ReadTrackingService {
  private notificationReboundSessionActive = false;

  markNotificationReboundPending(): void {
    this.notificationReboundSessionActive = true;
  }

  clearNotificationReboundSession(): void {
    this.notificationReboundSessionActive = false;
  }

  withTracking(params: Params = {}, payload: ReadTrackingPayload = {}): Params {
    const sessionEntry = payload.session_entry || this.resolveSessionEntry();

    return this.compactParams({
      ...params,
      source: payload.source,
      session_entry: sessionEntry,
      origin_type: payload.origin_type,
      origin_uuid: payload.origin_uuid,
      search_id: payload.search_id,
    });
  }

  withHomepageSource(params: Params = {}): Params {
    return this.withTracking(params, {
      source: 'homepage_app_open',
    });
  }

  withBannerSource(source: ReadTrackingBannerSource, params: Params = {}): Params {
    return this.withTracking(params, { source });
  }

  withSearchSource(params: Params = {}, searchId?: number): Params {
    return this.withTracking(params, {
      source: 'search',
      search_id: searchId,
    });
  }

  withNotificationSource(params: Params = {}): Params {
    return this.withTracking(params, {
      source: 'notification',
      session_entry: 'notification',
    });
  }

  withSuggestionSource(articleUuid: string, params: Params = {}): Params {
    return this.withTracking(params, {
      source: 'suggestion',
      origin_type: 'article',
      origin_uuid: articleUuid,
    });
  }

  withInternalNavigationSource(params: Params = {}): Params {
    return this.compactParams({
      ...params,
      source: 'internal_navigation',
      session_entry: this.resolveSessionEntry(),
      origin_type: undefined,
      origin_uuid: undefined,
      search_id: undefined,
    });
  }

  withArrowNavigationSource(params: Params = {}): Params {
    const source = this.normalizeSource(params['source']);
    const trackingListUuids = typeof params['tracking_list_uuids'] === 'string'
      ? params['tracking_list_uuids'].trim()
      : '';

    if (source && this.isBannerSource(source) && trackingListUuids !== '') {
      return this.compactParams({
        ...params,
        source,
        origin_type: undefined,
        origin_uuid: undefined,
        search_id: undefined,
      });
    }

    return this.withInternalNavigationSource(params);
  }

  extractPayload(params: Params = {}): ReadTrackingPayload {
    const payload: ReadTrackingPayload = {};
    const source = this.normalizeSource(params['source']);
    if (!source) {
      return payload;
    }

    payload.source = source;

    const originType = this.normalizeOriginType(params['origin_type']);
    if (originType) {
      payload.origin_type = originType;
    }

    const sessionEntry = this.normalizeSessionEntry(params['session_entry']);
    payload.session_entry = sessionEntry || this.resolveSessionEntry();

    if (typeof params['origin_uuid'] === 'string' && params['origin_uuid'].trim() !== '') {
      payload.origin_uuid = params['origin_uuid'].trim();
    }

    const searchId = this.normalizeSearchId(params['search_id']);
    if (typeof searchId === 'number') {
      payload.search_id = searchId;
    }

    return payload;
  }

  private resolveSessionEntry(): ReadTrackingSessionEntry {
    return this.notificationReboundSessionActive ? 'notification' : 'app_open';
  }

  private compactParams(params: Params): Params {
    return Object.keys(params || {}).reduce((acc, key) => {
      const value = params[key];
      if (value === undefined || value === null || value === '') {
        return acc;
      }

      acc[key] = value;
      return acc;
    }, {} as Params);
  }

  private normalizeSource(rawSource: unknown): ReadTrackingSource | undefined {
    if (typeof rawSource !== 'string') {
      return undefined;
    }

    const normalized = rawSource.trim();

    switch (normalized) {
      case 'notification':
      case 'homepage':
      case 'homepage_notification_rebound':
      case 'homepage_app_open':
      case 'search':
      case 'suggestion':
      case 'internal_navigation':
      case 'banner_elected_selection':
      case 'banner_top_articles':
      case 'banner_top_faq':
        return normalized as ReadTrackingSource;
      default:
        return undefined;
    }
  }

  private isBannerSource(source: ReadTrackingSource): source is ReadTrackingBannerSource {
    return (
      source === 'banner_elected_selection' ||
      source === 'banner_top_articles' ||
      source === 'banner_top_faq'
    );
  }

  private normalizeSessionEntry(rawSessionEntry: unknown): ReadTrackingSessionEntry | undefined {
    if (typeof rawSessionEntry !== 'string') {
      return undefined;
    }

    const normalized = rawSessionEntry.trim();

    switch (normalized) {
      case 'notification':
      case 'app_open':
        return normalized as ReadTrackingSessionEntry;
      default:
        return undefined;
    }
  }

  private normalizeOriginType(rawOriginType: unknown): ReadTrackingOriginType | undefined {
    if (typeof rawOriginType !== 'string') {
      return undefined;
    }

    const normalized = rawOriginType.trim();

    switch (normalized) {
      case 'article':
      case 'faq':
        return normalized as ReadTrackingOriginType;
      default:
        return undefined;
    }
  }

  private normalizeSearchId(rawSearchId: unknown): number | undefined {
    if (typeof rawSearchId === 'number' && Number.isFinite(rawSearchId)) {
      return rawSearchId;
    }

    if (typeof rawSearchId !== 'string') {
      return undefined;
    }

    const parsedSearchId = Number.parseInt(rawSearchId, 10);
    return Number.isFinite(parsedSearchId) ? parsedSearchId : undefined;
  }
}
